/**
 * <bo-carousel> — lightweight scroll-snap carousel used by the bo-* sections.
 * Markup: [data-track] scroll container, optional [data-prev]/[data-next] buttons and [data-dots].
 * Attributes: autoplay="5" (seconds) cycles slides; pauses on hover/focus.
 */
class BoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.dots = this.querySelector('[data-dots]');

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

    this.buildDots();
    this.onScroll();

    const seconds = Number(this.getAttribute('autoplay'));
    if (seconds > 0) {
      // Only advance while visible and not hovered/focused, so autoplay never moves a page the shopper has scrolled away from.
      this.visible = false;
      this.paused = false;
      this.observer = new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting), { threshold: 0.5 });
      this.observer.observe(this);
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
      this.timer = setInterval(() => {
        if (this.visible && !this.paused && !document.hidden) this.go(1, true);
      }, seconds * 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.observer?.disconnect();
  }

  get items() {
    return [...this.track.children];
  }

  /** Number of "pages" the track can be scrolled through. */
  get pageCount() {
    const { scrollWidth, clientWidth } = this.track;
    return Math.max(1, Math.round((scrollWidth - clientWidth) / clientWidth) + 1);
  }

  go(direction, wrap = false) {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;
    const atStart = scrollLeft <= 4;
    if (wrap && direction > 0 && atEnd) return this.track.scrollTo({ left: 0, behavior: 'smooth' });
    if (direction < 0 && atStart) return this.track.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    if (direction > 0 && atEnd) return this.track.scrollTo({ left: 0, behavior: 'smooth' });
    this.track.scrollBy({ left: direction * clientWidth, behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    for (let i = 0; i < this.pageCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bo-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () =>
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' })
      );
      this.dots.append(dot);
    }
  }

  onScroll() {
    if (!this.dots) return;
    const { scrollLeft, clientWidth } = this.track;
    const index = Math.round(scrollLeft / clientWidth);
    [...this.dots.children].forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));
  }
}

if (!customElements.get('bo-carousel')) customElements.define('bo-carousel', BoCarousel);
